import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { getTimezoneOffsetInHours } from 'www/utils'
import { REDIRECT_VALUES } from 'www/constants'
import type { ScheduleType } from 'www/api/apiScheduleTelemed.types'
import type { ApiScheduleNearestSlotsResponsePost } from 'www/api/apiScheduleNearestSlots.types'
import apiScheduleNearestSlots from 'www/api/apiScheduleNearestSlots'
import { APPOINTMENT_ERRORS } from 'www/vue_components/common/AppointmentPage/constants'
// eslint-disable-next-line import/no-cycle
import useCalendarDataStore from 'www/vue_components/common/AppointmentPage/stores/useCalendarDataStore'
import useDoctorDataStore from 'www/vue_components/common/AppointmentPage/stores/useDoctorDataStore'
import useLpuDataStore from 'www/vue_components/common/AppointmentPage/stores/useLpuDataStore'
import useAppointmentDataStore from 'www/vue_components/common/AppointmentPage/stores/useAppointmentDataStore'
import useGlobalDataStore from 'www/vue_components/common/AppointmentPage/stores/useGlobalDataStore'

export default defineStore('nearestSlotsStore', () => {
  const lpuDataStore = useLpuDataStore()
  const doctorDataStore = useDoctorDataStore()
  const calendarDataStore = useCalendarDataStore()
  const appointmentDataStore = useAppointmentDataStore()
  const globalDataStore = useGlobalDataStore()

  const selectedDate = computed(
    () => (calendarDataStore.editableCalendar.selectedDate.day && calendarDataStore.editableCalendar.selectedDate.time
      ? `${calendarDataStore.editableCalendar.selectedDate.day} ${calendarDataStore.editableCalendar.selectedDate.time}`
      : ''),
  )

  const data = ref<{
    lpu?: ApiScheduleNearestSlotsResponsePost[number]
    mt?: ApiScheduleNearestSlotsResponsePost[number]
  }>({})

  const isPending = ref(false)

  let prevScheduleType: ScheduleType

  function showError() {
    globalDataStore.updateErrorData({
      isDialogRender: true,
      isEnabled: true,
      ...APPOINTMENT_ERRORS.appointmentDateNotAvailable,
    })
  }

  async function update() {
    if (!selectedDate.value) {
      return
    }

    isPending.value = true

    try {
      if (lpuDataStore.lpuData.id && doctorDataStore.doctorData.id) {
        const response = (await apiScheduleNearestSlots.post({
          lpu_id: lpuDataStore.lpuData.id,
          doctor_id: doctorDataStore.doctorData.id,
          scan_mode: 'all',
          user_timezone: getTimezoneOffsetInHours(),
          user_target_dt: selectedDate.value,
        })).data
        const findSlotByType = (type: 'lpu' | 'mt') => response.find(nearestSlot => nearestSlot.type_schedule === type)

        data.value = {
          lpu: findSlotByType('lpu'),
          mt: findSlotByType('mt'),
        }
      }
    } catch {
      showError()
    } finally {
      isPending.value = false
    }
  }

  function isSlotSelected(type: 'mt' | 'lpu') {
    return data.value[type]?.nearest_dt === selectedDate.value
  }

  async function changePlaceTypeIfNeeded() {
    const isScheduleTypeChanged = prevScheduleType !== doctorDataStore.scheduleType

    prevScheduleType = doctorDataStore.scheduleType

    await update()

    const isNeedUpdateSlots = isScheduleTypeChanged && doctorDataStore.scheduleType === 'all'

    if (isNeedUpdateSlots) {
      calendarDataStore.requestForSlots({
        errorRequestCallback: showError,
      })
    }

    if (
      doctorDataStore.isTelemedMedtochkaAvailable
      && (
        !doctorDataStore.isTelemedAvailable
        || (
          appointmentDataStore.appointmentPlaceTypes.isTelemed
          && !isSlotSelected('lpu')
          && isSlotSelected('mt')
        )
      )
    ) {
      appointmentDataStore.updateInitAdditionalPlaceType(REDIRECT_VALUES.telemedMedtochka)

      if (doctorDataStore.scheduleType !== 'all') {
        calendarDataStore.requestForSlots({
          errorRequestCallback: showError,
        })
      }

      return true
    }

    if (
      doctorDataStore.isTelemedAvailable
      && (
        !doctorDataStore.isTelemedMedtochkaAvailable
        || (
          appointmentDataStore.appointmentPlaceTypes.isTelemedMedtochka
          && !isSlotSelected('mt')
          && isSlotSelected('lpu')
        )
      )
    ) {
      appointmentDataStore.updateInitAdditionalPlaceType(REDIRECT_VALUES.telemed)

      if (doctorDataStore.scheduleType !== 'all') {
        calendarDataStore.requestForSlots({
          errorRequestCallback: showError,
        })
      }

      return true
    }

    if (
      (isSlotSelected('mt') && !doctorDataStore.isTelemedMedtochkaAvailable)
      || (isSlotSelected('lpu') && !doctorDataStore.isTelemedAvailable)
    ) {
      calendarDataStore.resetSelectedDate()
      calendarDataStore.updateCalendarIsChanging(true)
    }

    return isNeedUpdateSlots
  }

  return {
    data,
    isPending,
    update,
    isSlotSelected,
    changePlaceTypeIfNeeded,
  }
})
